import { createContext, useContext } from 'react';
import { SecuredFlag } from '@components/security/types.ts';

/**
 * The assistant's context, its hook and the panel key — kept apart from AssistantProvider and
 * AssistantSidebar on purpose.
 *
 * A module that exports anything besides components cannot be hot-swapped by React Fast Refresh; Vite
 * re-executes it instead. When that module is the one calling createContext, the re-run creates a
 * *new* context: the provider still on screen keeps serving the old one while the menu reads the new
 * one, gets the default (permission not granted), and the assistant entry vanishes until a full
 * reload. Here, in a plain module that edits to the panel never touch, the context is created once.
 * (Dev-only: a production build runs every module exactly once.)
 */

export interface AssistantContextValue {
  /**
   * Visibility gate for every assistant entry point. A SecuredFlag rather than a boolean so the
   * callers can hand it to SecuredArea / a menu item's `secured` config and have the real permission
   * clause shown in security mode.
   */
  flag: SecuredFlag;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/** The assistant's key in the panel dock store (placement preference, minimized state). */
export const ASSISTANT_PANEL_KEY = 'assistant';

export const ASSISTANT_NOT_GRANTED: SecuredFlag = { id: 'menu-ai-assistant', granted: false, metadata: null };

export const AssistantContext = createContext<AssistantContextValue>({
  flag: ASSISTANT_NOT_GRANTED,
  isOpen: false,
  open: () => {},
  close: () => {},
});

export const useAssistant = (): AssistantContextValue => useContext(AssistantContext);
